import { Box } from '@mui/material'
import { AlertDialog, ProgressLoaders, SvgImage } from 'components/ui'
import { MESSAGES } from 'config'
import { useAuth } from 'context'
import { useSnackbar } from 'notistack'
import { useMemo, useState } from 'react'
import { CloseButton, ICModal, ICModalContent } from './CloudPay'
import { PayBillDialog } from './PayBillDialog'
import { styled } from '@mui/material/styles'

interface IInvoiceCloudFrame {
  page?: number
  children: React.ReactNode
}

// Enhanced modal content for invoice cloud with better responsiveness
const InvoiceCloudModalContent = styled(ICModalContent)(() => ({
  // Override the base styles for better InvoiceCloud iframe handling
  '@media (max-width: 768px)': {
    borderRadius: '0',
    maxWidth: '100%',
    maxHeight: '100%',
    height: '100vh',
    width: '100vw'
  },

  '@media (min-width: 769px)': {
    width: '95vw',
    height: '90vh',
    maxWidth: '1200px',
    maxHeight: '900px'
  }
}))

// Enhanced close button with better mobile responsiveness
const ResponsiveCloseButton = styled(CloseButton)(() => ({
  '@media (max-width: 768px)': {
    margin: '0.5rem',
    width: '2rem',
    height: '2rem',
    padding: '0.25rem'
  },

  '@media (min-width: 769px)': {
    margin: '0.75rem',
    width: '2rem',
    height: '2rem',
    padding: '0'
  }
}))

// Iframe container with proper responsive handling
const IframeContainer = styled(Box)({
  flex: 1,
  width: '100%',
  padding: '0 0.75rem 0.75rem',
  boxSizing: 'border-box',
  position: 'relative',

  '@media (max-width: 768px)': {
    padding: '0 0.5rem 0.5rem'
  }
})

// Loading overlay for iframe
const LoadingOverlay = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  zIndex: 1
}))

// Responsive iframe wrapper
const IframeWrapper = styled(Box)({
  width: '100%',
  height: '100%',
  minHeight: '500px',

  '& iframe': {
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: '0.25rem'
  },

  '@media (max-width: 768px)': {
    minHeight: '400px'
  }
})

export const InvoiceCloudFrame = ({ page, children }: IInvoiceCloudFrame): JSX.Element => {
  const { enqueueSnackbar } = useSnackbar()
  const [visible, setVisible] = useState<boolean>(false)
  const [refresher, setRefresher] = useState<number>(1)
  const { icAuth } = useAuth()
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)
  const [isPaperlessDialogOpen, setIsPaperlessDialogOpen] = useState<boolean>(false)
  const [isIframeLoaded, setIsIframeLoaded] = useState<boolean>(false)

  const handleDialog = (): void => {
    setIsDialogOpen(!isDialogOpen)
  }

  const handleShow = (): void => {
    if ((icAuth == null) && page !== 0) {
      enqueueSnackbar(MESSAGES.IC_RETRIEVE_ERROR, { variant: 'error' })
    } else if (icAuth != null) {
      setVisible(true)
    }

    if (page == null) {
      setVisible(false)
    }
  }

  const handleHide = (): void => {
    setVisible(false)
    setIsIframeLoaded(false)
    setRefresher(refresher + 1)
    if (page === 1) {
      setIsDialogOpen(true)
    } else if (page === 2) {
      setIsPaperlessDialogOpen(true)
    } else if (page === 9) {
      setIsDialogOpen(true)
    }
  }

  const iframeUrl = useMemo(() => {
    if (!visible || !icAuth) return ''
    // const cssUrl = `${window.location.origin}/invoicecloud-styles.css`

    return `https://www.invoicecloud.com/portal/remoteauth/customerportalredirect.aspx?RemoteAuthenticationToken=${icAuth}&ViewMode=1&RedirectID=${page}&CSSUrl=${process.env.REACT_APP_IC_STYLESHEET_URL}`
  }, [visible, icAuth, page])

  return (
    <>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          padding: 0,
          margin: 0,
          background: 'transparent',
          border: 'none',
          boxShadow: 'none',
          textTransform: 'none',
          fontFamily: 'var(--bs-body-font-family)',
          fontSize: 'var(--bs-font-size-m)'
        }}
        onClick={handleShow}
        component="div"
      >
        {children}
      </Box>

      <ICModal visible={visible ? 'true' : 'false'} onClick={handleHide}>
        <InvoiceCloudModalContent
          onClick={(e) => { e.stopPropagation() }}
        >
          <ResponsiveCloseButton
            onClick={handleHide}
          >
            <SvgImage name="CloseIcon" width={24} height={24} />
          </ResponsiveCloseButton>

          {(icAuth != null) && visible && (iframeUrl.length > 0) && (
            <IframeContainer>
              {!isIframeLoaded && (
                <LoadingOverlay>
                  <ProgressLoaders />
                </LoadingOverlay>
              )}
              <IframeWrapper>
                <iframe
                  key={refresher}
                  allow="payment"
                  id="frame"
                  src={iframeUrl}
                  title="InvoiceCloud frame"
                  onLoad={() => { setIsIframeLoaded(true) }}
                  style={{
                    opacity: isIframeLoaded ? 1 : 0,
                    transition: 'opacity 0.3s ease'
                  }}
                />
              </IframeWrapper>
            </IframeContainer>
          )}
        </InvoiceCloudModalContent>
      </ICModal>

      <PayBillDialog isOpen={isDialogOpen} handleDialog={handleDialog} />
      <AlertDialog
        open={isPaperlessDialogOpen}
        handleClick={() => { setIsPaperlessDialogOpen(!isPaperlessDialogOpen) }}
        type="info"
        message={MESSAGES.IC_PAPERLESS_INFO}
      />
    </>
  )
}
